import * as React from "react";
import { Field, FieldProps } from "formik";
import { GroupRole } from "../../../../application/domain/group/group";
import { Roles } from "../../../../application/domain/rbac/rbac";
import { useState } from "react";
import { toast } from "react-toastify";
import { reportUnexpectedError } from "../../../../error-reporter";
import { UserAuthMethod } from "../../../../application/domain/user/user";
import {
  gqlClient,
  gqlHeader,
  runGqlQueryV2,
} from "../../../../graphql/client";
import { GroupSelector } from "../../../molecules/group-selector";
import { RBACHidden } from "../../../helpers/rbac/rbac";
import {
  WarningBanner,
  RadioButton,
  TextField,
  Checkbox,
} from "@graffer-inc/console-design-kit";
import { UserRolesSelector } from "../../../molecules/user-role-selector";
import { useAuthContextV2 } from "../../../../auth-state";
import { UsersService } from "../../../../application/service/smart-apply/users/users-service";

export type UserCreateValues = {
  name: string;
  accountName: string;
  authMethod: UserAuthMethod;
  password: string;
  doSendInvitation: boolean;
  externalUserId: string;
  roles: Roles[];
  groupsRoles: Record<string, GroupRole>;
  emails: string[];
};

export const UserCreate: React.FC<{
  values: UserCreateValues;
  isServiceProviderAccount: boolean;
  orgId: string;
}> = ({ values, isServiceProviderAccount, orgId }) => {
  const [hasOrgSAMLSetting, setHasOrgSAMLSetting] = useState(false);
  const [disableSendInvitation, setDisableSendInvitation] = useState(true);
  const [isPasswordPwned, setIsPasswordPwned] = useState<boolean>(false);

  const { auth } = useAuthContextV2();

  const shouldSelectGroup = React.useMemo(() => {
    return values.roles.includes("Staff");
  }, [values.roles]);

  const usersService = new UsersService();

  const fetchOrgSAMLSetting = React.useCallback(async () => {
    if (!orgId) {
      return;
    }
    try {
      const res = await runGqlQueryV2(() =>
        gqlClient.orgSamlSetting({ orgID: orgId }, gqlHeader("q")),
      );
      if (res.error) {
        toast.error("SAML設定情報の取得に失敗しました。");
        return;
      }
      setHasOrgSAMLSetting(!!res.data?.orgSamlSetting);
    } catch (e) {
      reportUnexpectedError(e);
    }
  }, [orgId]);

  React.useEffect(() => {
    if (auth.data?.orgUsingFeature.useSamlAuth) {
      fetchOrgSAMLSetting();
    }
  }, [fetchOrgSAMLSetting, auth]);

  return (
    <RBACHidden
      requirePermissions="users.create"
      hiddenChildren={
        <WarningBanner message="ユーザーを登録する権限がありません。" />
      }
    >
      <div className="field">
        <Field className="control" name="name">
          {({ field, form }: FieldProps) => (
            <TextField
              label="氏名"
              description=""
              placeholder="氏名"
              errorMessages={
                form.touched.name ? form.errors.name?.toString() : ""
              }
              required
              {...field}
            />
          )}
        </Field>
      </div>
      <div className="field">
        <Field className="control" name="accountName">
          {({ field, form }: FieldProps) => (
            <TextField
              label="アカウント名"
              description=""
              placeholder="アカウント名"
              errorMessages={
                form.touched.accountName
                  ? form.errors.accountName?.toString()
                  : ""
              }
              required
              {...field}
            />
          )}
        </Field>
      </div>
      <div className="field">
        <Field name="emails[0]">
          {({ field, form }: FieldProps) => (
            <TextField
              label="メールアドレス"
              description=""
              placeholder="example@graffer.jp"
              errorMessages={form.errors.emails?.toString()}
              {...field}
              onBlur={async (e) => {
                // 有効なメールアドレスの入力がある場合
                if (!!e.target.value && !form.errors.emails) {
                  setDisableSendInvitation(false);
                  return;
                }
                // 入力がないor無効なメールアドレスの場合
                await form.setFieldValue("doSendInvitation", false);
                setDisableSendInvitation(true);
              }}
            />
          )}
        </Field>
      </div>
      {auth.data?.orgUsingFeature.useSamlAuth && (
        <div className="field">
          <Field className="control" name="authMethod">
            {({ form }: FieldProps) => (
              <>
                <RadioButton
                  description=""
                  label="認証方法"
                  onChange={(e) => {
                    form.setValues({
                      ...form.values,
                      authMethod: e.target.value,
                    });
                  }}
                  options={[
                    {
                      name: "パスワード認証",
                      value: "password",
                    },
                    ...(hasOrgSAMLSetting
                      ? [
                          {
                            name: "SAML認証",
                            value: "saml",
                          },
                        ]
                      : []),
                  ]}
                  value={form.values.authMethod}
                  required
                />
              </>
            )}
          </Field>
        </div>
      )}
      {values.authMethod === "password" && (
        <>
          <div className="field">
            <Field className="control" name="password">
              {({ field, form }: FieldProps) => (
                <>
                  <TextField
                    label="仮パスワード"
                    description="有効期限は1週間です。有効期限内に仮パスワードから変更されない場合はパスワードリセットが必要です。"
                    placeholder="仮パスワード"
                    errorMessages={
                      form.touched.password
                        ? form.errors.password?.toString()
                        : ""
                    }
                    required
                    {...field}
                    onChange={async (e) => {
                      form.setFieldValue("password", e.target.value);

                      if (form.errors.password) {
                        return;
                      }

                      const isPwned = await usersService.isPwnedPassword(
                        e.target.value,
                      );
                      if (isPwned) {
                        setIsPasswordPwned(true);
                      } else {
                        setIsPasswordPwned(false);
                      }
                    }}
                  />
                  {isPasswordPwned && (
                    <WarningBanner
                      message="推測されやすいパスワードです"
                      description="入力されたパスワードは推測されやすいパスワードです。大文字、小文字、数字、記号を組み合わせたパスワードの設定をおすすめします。"
                    />
                  )}
                </>
              )}
            </Field>
          </div>
          <div className="field">
            <Field className="control" name="doSendInvitation">
              {({ field, form }: FieldProps) => (
                <Checkbox
                  description="メールアドレスが入力されている場合に仮パスワードを含むメールを送信します。"
                  label="仮パスワードをメールで送信"
                  options={[
                    {
                      value: "doSendInvitation",
                      name: "仮パスワードをメールで送信する",
                    },
                  ]}
                  disabled={disableSendInvitation}
                  values={field.value ? ["doSendInvitation"] : []}
                  onChange={() => {
                    form.setFieldValue("doSendInvitation", !field.value);
                  }}
                />
              )}
            </Field>
          </div>
        </>
      )}
      {values.authMethod === "saml" && (
        <div className="field">
          <Field className="control" name="externalUserId">
            {({ field, form }: FieldProps) => (
              <TextField
                label="外部ユーザーID"
                description="SAML認証のIDプロバイダー側におけるユーザーIDを入力してください。"
                placeholder="外部ユーザーID"
                errorMessages={
                  form.touched.externalUserId
                    ? form.errors.externalUserId?.toString()
                    : ""
                }
                required
                {...field}
              />
            )}
          </Field>
        </div>
      )}
      <div className="field">
        <label className="label">ロール設定</label>
        <Field className="control" name="roles">
          {({ field, form }: FieldProps) => (
            <UserRolesSelector
              values={field.value}
              onChange={(e) => {
                if (e.target.checked) {
                  form.setValues({
                    ...form.values,
                    [field.name]: [...field.value, e.target.value],
                    groupsRoles:
                      e.target.value !== "Staff" ? {} : form.values.groupsRoles,
                  });
                } else {
                  form.setValues({
                    ...form.values,
                    [field.name]: field.value.filter(
                      (v: Roles) => v !== e.target.value,
                    ),
                  });
                }
              }}
              errorMessages={form.errors.roles?.toString()}
              canSelectAdmin={isServiceProviderAccount}
            />
          )}
        </Field>
      </div>
      {shouldSelectGroup ? (
        <div className="field">
          <label className="label">所属グループ</label>
          <RBACHidden
            requirePermissions="users.manage_groups"
            hiddenChildren={
              <WarningBanner message="所属グループを変更する権限がありません。" />
            }
          >
            <Field className="control" name="groupsRoles">
              {({ field, form }: FieldProps<{ [key: string]: GroupRole }>) => {
                const editingGroupRoles: Record<string, GroupRole> =
                  Object.keys(field.value).length > 0
                    ? field.value
                    : {
                        "": "Staff",
                      };
                return (
                  <div className="field">
                    {form.errors.groupRoles?.toString()}
                    <GroupSelector
                      editingGroupRoles={editingGroupRoles}
                      setGroupRoles={(groupRoles) => {
                        form.setFieldTouched(field.name);
                        form.setFieldValue(field.name, groupRoles);
                      }}
                      validationError={form.errors.groupsRoles?.toString()}
                      orgId={orgId}
                      isServiceProvider={isServiceProviderAccount}
                    />
                  </div>
                );
              }}
            </Field>
          </RBACHidden>
        </div>
      ) : (
        <p className="control">
          特権管理者およびアカウント管理者はグループに所属しません。
        </p>
      )}
    </RBACHidden>
  );
};
